import { useState, useRef, useEffect } from "react";
import { Pencil, Check, X, Download, GraduationCap, Smartphone, Shield, Quote, Star, ArrowRight, Search, BookOpen, Users, Award, ChevronRight } from "lucide-react";

const EditableText = ({ value, onChange, tag = "p", className = "", multiline = false, placeholder = "Click to edit..." }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const save = () => { onChange(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) {
    return (
      <div className="relative group/edit inline-block w-full">
        {multiline ? (
          <textarea
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") cancel(); }}
            rows={3}
            className="w-full bg-white/90 dark:bg-gray-800/90 border-2 border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
            data-testid="inline-edit-textarea"
          />
        ) : (
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="w-full bg-white/90 dark:bg-gray-800/90 border-2 border-blue-500 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="inline-edit-input"
          />
        )}
        <div className="flex gap-1 mt-1">
          <button onClick={save} className="bg-blue-600 text-white rounded px-2 py-0.5 text-xs flex items-center gap-1 hover:bg-blue-700" data-testid="inline-edit-save"><Check className="w-3 h-3" /> Save</button>
          <button onClick={cancel} className="bg-gray-500 text-white rounded px-2 py-0.5 text-xs flex items-center gap-1 hover:bg-gray-600" data-testid="inline-edit-cancel"><X className="w-3 h-3" /> Cancel</button>
        </div>
      </div>
    );
  }

  const Tag = tag;
  return (
    <span
      className={`group/editable relative cursor-pointer inline-block ${className}`}
      onClick={() => setEditing(true)}
      title="Click to edit"
      data-testid={`editable-text-${placeholder}`}
    >
      <Tag className="inline">{value || <span className="opacity-40 italic">{placeholder}</span>}</Tag>
      <Pencil className="w-3.5 h-3.5 absolute -right-5 top-0.5 opacity-0 group-hover/editable:opacity-100 text-blue-500 transition-opacity" />
    </span>
  );
};

const FEATURE_ICONS = [Download, GraduationCap, Smartphone, Shield];
const FEATURE_DEFAULTS = [
  { title: "Instant Delivery", desc: "Get your mock exams within 60 seconds. PDF downloads delivered straight to your email." },
  { title: "GES Aligned", desc: "All content aligned with Ghana Education Service syllabus (2024 to date)." },
  { title: "Mobile Money", desc: "Pay easily with MTN MoMo, Vodafone Cash, or AirtelTigo Money." },
  { title: "Secure Downloads", desc: "Watermarked PDFs for your protection with buyer-specific security." },
];
const TESTIMONIAL_DEFAULTS = [
  { name: "Mrs. Abena Mensah", role: "JHS Teacher, Accra", content: "The mock exams are perfectly aligned with the GES syllabus. My students' performance has improved significantly since we started using them." },
  { name: "Kwame Asante", role: "Basic 9 Student", content: "I passed my BECE with flying colors! The practice questions were very similar to the actual exam. Highly recommended." },
  { name: "Dr. Isaac Owusu", role: "School Administrator", content: "Bulk ordering for our school was seamless. Great quality materials at affordable prices. Our pass rate went up 20%." },
];
const STAT_DEFAULTS = [
  { value: "500+", label: "Downloads" },
  { value: "50+", label: "Partner Schools" },
  { value: "95%", label: "Pass Rate" },
  { value: "24/7", label: "Access" },
];

export function LivePreviewEditor({ config, onChange, darkMode: dm, sectionConfig }) {
  const blocks = config?.content_blocks || {};
  const images = config?.images || {};

  const get = (key, fallback = "") => blocks[key] ?? fallback;
  const set = (key, val) => onChange(key, val);
  const isOn = (key) => {
    if (!sectionConfig?.[key]) return true;
    return sectionConfig[key].enabled !== false;
  };

  const heroBg = images.hero || "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/n4jmpo8p_WhatsApp%20Image%202026-02-09%20at%209.31.47%20PM.jpeg";

  return (
    <div className="space-y-0 rounded-xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 bg-white" data-testid="live-preview-editor">

      {/* ═══ HEADER BAR ═══ */}
      <div className="bg-[#0f172a] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-6 w-6 object-contain" />
          <span className="font-bold text-sm">Great Messiah Publication</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-300">
          <span>Home</span><span>Mock Exams</span><span>For Schools</span><span>About</span>
        </div>
      </div>

      {/* ═══ HERO SECTION ═══ */}
      {isOn("hero") && (
        <div className="relative bg-[#0f172a] text-white overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url('${heroBg}')` }} />
          <div className="relative z-10 px-8 py-16 max-w-3xl">
            <div className="inline-block bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <EditableText value={get("hero_badge", "Trusted by 50+ Schools in Ghana")} onChange={v => set("hero_badge", v)} className="text-white/90 text-xs font-medium" tag="span" placeholder="Hero Badge" />
            </div>
            <div className="mb-2">
              <EditableText value={get("hero_title", "Achieve the Best")} onChange={v => set("hero_title", v)} className="text-white text-3xl font-bold" tag="span" placeholder="Hero Title" />
              {" "}
              <EditableText value={get("hero_highlight", "BECE Results")} onChange={v => set("hero_highlight", v)} className="text-red-500 text-3xl font-bold" tag="span" placeholder="Hero Highlight" />
            </div>
            <div className="mb-6">
              <EditableText value={get("hero_subtitle", "Quality mock examinations designed by experienced educators. Instant digital delivery with comprehensive marking schemes.")} onChange={v => set("hero_subtitle", v)} className="text-gray-300 text-sm leading-relaxed" tag="span" multiline placeholder="Hero Subtitle" />
            </div>
            <div className="flex gap-3">
              <span className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1">
                <EditableText value={get("hero_cta_primary", "Browse Mock Exams")} onChange={v => set("hero_cta_primary", v)} className="text-white text-sm" tag="span" placeholder="Primary CTA" />
                <ArrowRight className="w-4 h-4" />
              </span>
              <span className="border border-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium">
                <EditableText value={get("hero_cta_secondary", "School Bulk Orders")} onChange={v => set("hero_cta_secondary", v)} className="text-white text-sm" tag="span" placeholder="Secondary CTA" />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STATS ═══ */}
      {isOn("stats") && (
        <div className="bg-[#0f172a] border-t border-white/10">
          <div className="grid grid-cols-4 divide-x divide-white/10 px-4 py-6">
            {STAT_DEFAULTS.map((stat, i) => (
              <div key={i} className="text-center">
                <EditableText value={get(`stat_${i+1}_value`, stat.value)} onChange={v => set(`stat_${i+1}_value`, v)} className="text-white text-2xl font-bold" tag="span" placeholder={`Stat ${i+1} Value`} />
                <div className="mt-1">
                  <EditableText value={get(`stat_${i+1}_label`, stat.label)} onChange={v => set(`stat_${i+1}_label`, v)} className="text-gray-400 text-xs" tag="span" placeholder={`Stat ${i+1} Label`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ ABOUT ═══ */}
      {isOn("about") && (
        <div className="bg-white px-8 py-12">
          <div className="max-w-2xl">
            <EditableText value={get("about_label", "About Us")} onChange={v => set("about_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider mb-2" tag="span" placeholder="About Label" />
            <div className="mt-3 mb-4">
              <EditableText value={get("about_title", "Empowering Ghana's Future Leaders")} onChange={v => set("about_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="About Title" />
            </div>
            <EditableText value={get("about_description", "Great Messiah Publication is dedicated to providing top-quality BECE preparation materials that align with the Ghana Education Service curriculum. Our mock exams are crafted by experienced educators who understand what it takes to succeed.")} onChange={v => set("about_description", v)} className="text-gray-600 text-sm leading-relaxed" tag="span" multiline placeholder="About Description" />
          </div>
        </div>
      )}

      {/* ═══ FEATURES / WHY CHOOSE US ═══ */}
      {isOn("features") && (
        <div className="bg-gray-50 px-8 py-12">
          <div className="text-center mb-8">
            <EditableText value={get("features_label", "Why Choose Us")} onChange={v => set("features_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider" tag="span" placeholder="Features Label" />
            <div className="mt-3">
              <EditableText value={get("features_title", "Everything You Need to Excel")} onChange={v => set("features_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="Features Title" />
            </div>
            <div className="mt-2">
              <EditableText value={get("features_subtitle", "Join thousands of successful students who trust Great Messiah Publication")} onChange={v => set("features_subtitle", v)} className="text-gray-500 text-sm" tag="span" placeholder="Features Subtitle" />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURE_DEFAULTS.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={i} className="bg-white p-5 rounded-xl border border-gray-100 text-center">
                  <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-[#0f172a]" />
                  </div>
                  <EditableText value={get(`feature_${i+1}_title`, f.title)} onChange={v => set(`feature_${i+1}_title`, v)} className="text-[#0f172a] text-sm font-bold mb-1" tag="span" placeholder={`Feature ${i+1} Title`} />
                  <div className="mt-2">
                    <EditableText value={get(`feature_${i+1}_desc`, f.desc)} onChange={v => set(`feature_${i+1}_desc`, v)} className="text-gray-500 text-xs leading-relaxed" tag="span" multiline placeholder={`Feature ${i+1} Description`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ FEATURED PRODUCTS ═══ */}
      {isOn("featured_products") && (
        <div className="bg-white px-8 py-12">
          <div className="text-center mb-6">
            <EditableText value={get("featured_label", "Popular Products")} onChange={v => set("featured_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider" tag="span" placeholder="Featured Label" />
            <div className="mt-3">
              <EditableText value={get("featured_title", "Trending This Month")} onChange={v => set("featured_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="Featured Title" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                <div className="h-28 bg-gray-200 rounded-lg mb-3 flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-gray-400" />
                </div>
                <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
                <div className="h-2 bg-gray-100 rounded w-1/2 mx-auto" />
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-3 italic">Products are loaded dynamically from your catalog</p>
        </div>
      )}

      {/* ═══ TESTIMONIALS ═══ */}
      {isOn("testimonials") && (
        <div className="bg-gray-50 px-8 py-12">
          <div className="text-center mb-8">
            <EditableText value={get("testimonials_label", "Success Stories")} onChange={v => set("testimonials_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider" tag="span" placeholder="Testimonials Label" />
            <div className="mt-3">
              <EditableText value={get("testimonials_title", "Trusted by Students & Teachers")} onChange={v => set("testimonials_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="Testimonials Title" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {TESTIMONIAL_DEFAULTS.map((t, i) => (
              <div key={i} className="bg-white p-5 rounded-xl border border-gray-100">
                <Quote className="w-5 h-5 text-red-500/30 mb-2" />
                <EditableText value={get(`testimonial_${i+1}_content`, t.content)} onChange={v => set(`testimonial_${i+1}_content`, v)} className="text-gray-600 text-xs leading-relaxed" tag="span" multiline placeholder={`Testimonial ${i+1}`} />
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <EditableText value={get(`testimonial_${i+1}_name`, t.name)} onChange={v => set(`testimonial_${i+1}_name`, v)} className="text-[#0f172a] text-xs font-bold" tag="span" placeholder={`Name ${i+1}`} />
                  <div className="mt-0.5">
                    <EditableText value={get(`testimonial_${i+1}_role`, t.role)} onChange={v => set(`testimonial_${i+1}_role`, v)} className="text-gray-400 text-[10px]" tag="span" placeholder={`Role ${i+1}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ CTA ═══ */}
      {isOn("cta") && (
        <div className="relative bg-[#0f172a] text-white px-8 py-12 text-center overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url('${images.examHall || ""}')` }} />
          <div className="relative z-10">
            <EditableText value={get("cta_title", "Ready to Start Preparing?")} onChange={v => set("cta_title", v)} className="text-white text-2xl font-bold" tag="span" placeholder="CTA Title" />
            <div className="mt-3 mb-6">
              <EditableText value={get("cta_subtitle", "Get instant access to quality mock examinations. Individual exams start at just GHS 15.00.")} onChange={v => set("cta_subtitle", v)} className="text-gray-300 text-sm" tag="span" multiline placeholder="CTA Subtitle" />
            </div>
            <div className="flex justify-center gap-3">
              <span className="bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">
                <EditableText value={get("cta_primary", "Browse Mock Exams")} onChange={v => set("cta_primary", v)} className="text-white text-sm" tag="span" placeholder="CTA Primary Button" />
              </span>
              <span className="border border-white/30 text-white px-5 py-2.5 rounded-lg text-sm font-medium">
                <EditableText value={get("cta_secondary", "Contact for Bulk Orders")} onChange={v => set("cta_secondary", v)} className="text-white text-sm" tag="span" placeholder="CTA Secondary Button" />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* ═══ SUCCESS STORIES PAGE ═══ */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="bg-[#0f172a] text-white px-6 py-2 text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">--- Success Stories Page ---</span>
      </div>

      {/* SS Hero */}
      <div className="relative bg-[#0f172a] text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/6a54yvph_WhatsApp%20Image%202026-02-09%20at%2011.16.41%20PM.jpeg')" }} />
        <div className="relative z-10 px-8 py-12 max-w-3xl">
          <div className="inline-block bg-white/10 rounded-full px-4 py-1.5 mb-4">
            <EditableText value={get("ss_hero_badge", "Proven Results Across Ghana")} onChange={v => set("ss_hero_badge", v)} className="text-white/90 text-xs font-medium" tag="span" placeholder="SS Hero Badge" />
          </div>
          <div className="mb-1">
            <EditableText value={get("ss_hero_title", "Real Students.")} onChange={v => set("ss_hero_title", v)} className="text-white text-3xl font-bold" tag="span" placeholder="SS Hero Title" />
            {" "}
            <EditableText value={get("ss_hero_highlight", "Real Results.")} onChange={v => set("ss_hero_highlight", v)} className="text-red-500 text-3xl font-bold" tag="span" placeholder="SS Hero Highlight" />
          </div>
          <div className="mt-4 mb-6">
            <EditableText value={get("ss_hero_subtitle", "See how schools and students across Ghana have achieved remarkable BECE improvements with Great Messiah Publication mock exams.")} onChange={v => set("ss_hero_subtitle", v)} className="text-gray-300 text-sm leading-relaxed" tag="span" multiline placeholder="SS Hero Subtitle" />
          </div>
          <span className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1">
            <EditableText value={get("ss_hero_cta", "Start Your Journey")} onChange={v => set("ss_hero_cta", v)} className="text-white text-sm" tag="span" placeholder="SS Hero CTA" />
          </span>
        </div>
      </div>

      {/* SS Stats */}
      <div className="bg-white px-4 py-8">
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="text-center">
              <EditableText value={get(`ss_stat_${i}_value`, ["95%","50+","28%","3000+"][i-1])} onChange={v => set(`ss_stat_${i}_value`, v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder={`SS Stat ${i} Value`} />
              <div className="mt-1">
                <EditableText value={get(`ss_stat_${i}_label`, ["Average Pass Rate","Partner Schools","Avg. Score Improvement","Students Helped"][i-1])} onChange={v => set(`ss_stat_${i}_label`, v)} className="text-gray-500 text-xs uppercase" tag="span" placeholder={`SS Stat ${i} Label`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SS School Performance */}
      <div className="bg-gray-50 px-8 py-10">
        <div className="text-center mb-6">
          <EditableText value={get("ss_schools_label", "School Performance")} onChange={v => set("ss_schools_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider" tag="span" placeholder="SS Schools Label" />
          <div className="mt-2">
            <EditableText value={get("ss_schools_title", "Schools That Transformed Results")} onChange={v => set("ss_schools_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="SS Schools Title" />
          </div>
          <div className="mt-2">
            <EditableText value={get("ss_schools_subtitle", "Compare BECE pass rates before and after partnering with Great Messiah Publication")} onChange={v => set("ss_schools_subtitle", v)} className="text-gray-500 text-sm" tag="span" multiline placeholder="SS Schools Subtitle" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => {
            const defaults = [
              {name:"Accra Academy JHS",loc:"Greater Accra",before:"62",after:"89",students:"120",year:"2025",quote:"Great Messiah's mock exams transformed our exam preparation.",contact:"Mr. Emmanuel Adjei, Headmaster"},
              {name:"Kumasi Methodist JHS",loc:"Ashanti Region",before:"55",after:"83",students:"85",year:"2025",quote:"Our students gained confidence knowing exactly what to expect.",contact:"Mrs. Grace Owusu, Academic Head"},
              {name:"Tamale Central JHS",loc:"Northern Region",before:"48",after:"76",students:"200",year:"2025",quote:"Even in remote areas, digital delivery gave us instant access.",contact:"Alhaji Ibrahim Yakubu, Director"},
            ][i-1];
            return (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                <EditableText value={get(`ss_school_${i}_name`, defaults.name)} onChange={v => set(`ss_school_${i}_name`, v)} className="text-[#0f172a] text-sm font-bold" tag="span" placeholder={`School ${i} Name`} />
                <div className="mt-1"><EditableText value={get(`ss_school_${i}_location`, defaults.loc)} onChange={v => set(`ss_school_${i}_location`, v)} className="text-gray-400 text-xs" tag="span" placeholder={`School ${i} Location`} /></div>
                <div className="flex items-center gap-3 mt-3 text-xs">
                  <span className="text-gray-400">Before: </span><EditableText value={get(`ss_school_${i}_before`, defaults.before)} onChange={v => set(`ss_school_${i}_before`, v)} className="text-gray-600 text-xs font-bold" tag="span" placeholder="Before %" />
                  <span className="text-red-500">After: </span><EditableText value={get(`ss_school_${i}_after`, defaults.after)} onChange={v => set(`ss_school_${i}_after`, v)} className="text-red-600 text-xs font-bold" tag="span" placeholder="After %" />
                </div>
                <div className="mt-2"><EditableText value={get(`ss_school_${i}_quote`, defaults.quote)} onChange={v => set(`ss_school_${i}_quote`, v)} className="text-gray-500 text-[10px] italic leading-relaxed" tag="span" multiline placeholder={`School ${i} Quote`} /></div>
                <div className="mt-2 flex gap-2">
                  <EditableText value={get(`ss_school_${i}_students`, defaults.students)} onChange={v => set(`ss_school_${i}_students`, v)} className="text-gray-400 text-[10px]" tag="span" placeholder="Students" />
                  <span className="text-gray-300 text-[10px]">|</span>
                  <EditableText value={get(`ss_school_${i}_contact`, defaults.contact)} onChange={v => set(`ss_school_${i}_contact`, v)} className="text-gray-400 text-[10px]" tag="span" placeholder="Contact" />
                </div>
                <div className="mt-1"><EditableText value={get(`ss_school_${i}_year`, defaults.year)} onChange={v => set(`ss_school_${i}_year`, v)} className="text-gray-300 text-[10px]" tag="span" placeholder="Year" /></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SS Student Journeys */}
      <div className="bg-white px-8 py-10">
        <div className="mb-6">
          <EditableText value={get("ss_students_label", "Student Journeys")} onChange={v => set("ss_students_label", v)} className="text-red-600 text-xs font-semibold uppercase tracking-wider" tag="span" placeholder="SS Students Label" />
          <div className="mt-2">
            <EditableText value={get("ss_students_title", "From Struggling to Excelling")} onChange={v => set("ss_students_title", v)} className="text-[#0f172a] text-2xl font-bold" tag="span" placeholder="SS Students Title" />
          </div>
        </div>
        {/* Floating badge preview */}
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-accent text-white rounded-xl p-3 shadow-lg inline-block">
            <EditableText value={get("ss_stat_3_value", "28%")} onChange={v => set("ss_stat_3_value", v)} className="text-white text-2xl font-bold" tag="span" placeholder="Improvement %" />
            <div className="mt-0.5">
              <EditableText value={get("ss_improvement_label", "Avg. Improvement")} onChange={v => set("ss_improvement_label", v)} className="text-white/80 text-xs" tag="span" placeholder="Badge Label" />
            </div>
          </div>
          <span className="text-gray-400 text-xs italic">← Badge overlaying student photo</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1,2,3,4].map(i => {
            const defaults = [
              {name:"Abena Mensah",school:"Osu Presbyterian JHS",before:"Aggregate 24",after:"Aggregate 8",subjects:"Mathematics, English, Science",quote:"The practice questions were very similar to the actual BECE."},
              {name:"Kwame Asante",school:"Achimota Basic School",before:"Aggregate 20",after:"Aggregate 10",subjects:"Social Studies, RME, ICT",quote:"The marking schemes helped me understand exactly how examiners want answers."},
              {name:"Fatima Ibrahim",school:"Tamale International School",before:"Aggregate 30",after:"Aggregate 12",subjects:"All Core Subjects",quote:"After doing the mock exams multiple times, I finally understood the exam pattern."},
              {name:"Daniel Tetteh",school:"Ho EP JHS",before:"Aggregate 22",after:"Aggregate 9",subjects:"Mathematics, French",quote:"My mathematics score went from a 5 to a 2. The solutions were a game-changer."},
            ][i-1];
            return (
              <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <EditableText value={get(`ss_student_${i}_name`, defaults.name)} onChange={v => set(`ss_student_${i}_name`, v)} className="text-[#0f172a] text-sm font-bold" tag="span" placeholder={`Student ${i} Name`} />
                <div className="mt-0.5"><EditableText value={get(`ss_student_${i}_school`, defaults.school)} onChange={v => set(`ss_student_${i}_school`, v)} className="text-gray-400 text-xs" tag="span" placeholder={`Student ${i} School`} /></div>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-[10px]"><EditableText value={get(`ss_student_${i}_before`, defaults.before)} onChange={v => set(`ss_student_${i}_before`, v)} className="text-gray-600 text-[10px]" tag="span" placeholder="Before" /></span>
                  <span className="text-green-600 text-xs">→</span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold"><EditableText value={get(`ss_student_${i}_after`, defaults.after)} onChange={v => set(`ss_student_${i}_after`, v)} className="text-green-700 text-[10px]" tag="span" placeholder="After" /></span>
                </div>
                <div className="mt-1"><EditableText value={get(`ss_student_${i}_subjects`, defaults.subjects)} onChange={v => set(`ss_student_${i}_subjects`, v)} className="text-gray-400 text-[10px]" tag="span" placeholder="Subjects" /></div>
                <div className="mt-2"><EditableText value={get(`ss_student_${i}_quote`, defaults.quote)} onChange={v => set(`ss_student_${i}_quote`, v)} className="text-gray-500 text-[10px] italic leading-relaxed" tag="span" multiline placeholder={`Student ${i} Quote`} /></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SS CTA */}
      <div className="relative bg-[#0f172a] text-white px-8 py-10 text-center overflow-hidden">
        <EditableText value={get("ss_cta_title", "Your Success Story Starts Here")} onChange={v => set("ss_cta_title", v)} className="text-white text-2xl font-bold" tag="span" placeholder="SS CTA Title" />
        <div className="mt-3 mb-6">
          <EditableText value={get("ss_cta_subtitle", "Join thousands of students and schools who have improved their BECE results with our GES-aligned mock examinations.")} onChange={v => set("ss_cta_subtitle", v)} className="text-gray-300 text-sm" tag="span" multiline placeholder="SS CTA Subtitle" />
        </div>
        <div className="flex justify-center gap-3">
          <span className="bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium">
            <EditableText value={get("ss_cta_primary", "Browse Mock Exams")} onChange={v => set("ss_cta_primary", v)} className="text-white text-sm" tag="span" placeholder="SS CTA Primary" />
          </span>
          <span className="border border-white/30 text-white px-5 py-2 rounded-lg text-sm font-medium">
            <EditableText value={get("ss_cta_secondary", "Partner With Us")} onChange={v => set("ss_cta_secondary", v)} className="text-white text-sm" tag="span" placeholder="SS CTA Secondary" />
          </span>
        </div>
      </div>

      {/* ═══ FOOTER ═══ */}
      <div className="bg-[#0f172a] text-white px-6 py-2 text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">--- Footer ---</span>
      </div>
      <div className="bg-[#0f172a] text-white px-8 py-8">
        <div className="grid grid-cols-4 gap-6">
          {/* Brand */}
          <div>
            <EditableText value={get("footer_brand_name", "Great Messiah Publication")} onChange={v => set("footer_brand_name", v)} className="text-white text-lg font-bold" tag="span" placeholder="Brand Name" />
            <div className="mt-3">
              <EditableText value={get("footer_brand_desc", "Empowering Ghanaian students with quality BECE preparation materials. GES-aligned mock examinations for academic excellence.")} onChange={v => set("footer_brand_desc", v)} className="text-gray-400 text-xs leading-relaxed" tag="span" multiline placeholder="Brand Description" />
            </div>
          </div>
          {/* Products placeholder */}
          <div>
            <p className="text-sm font-semibold mb-2">Products</p>
            <p className="text-gray-500 text-[10px] italic">Navigation links (auto-generated)</p>
          </div>
          {/* Support placeholder */}
          <div>
            <p className="text-sm font-semibold mb-2">Support</p>
            <p className="text-gray-500 text-[10px] italic">Navigation links (auto-generated)</p>
          </div>
          {/* Contact */}
          <div>
            <EditableText value={get("footer_contact_heading", "Contact Us")} onChange={v => set("footer_contact_heading", v)} className="text-white text-sm font-semibold" tag="span" placeholder="Contact Heading" />
            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-gray-400 text-xs shrink-0">Location:</span>
                <EditableText value={get("footer_address", "Accra, Ghana\nGreater Accra Region")} onChange={v => set("footer_address", v)} className="text-gray-400 text-xs" tag="span" multiline placeholder="Address" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs shrink-0">Phone:</span>
                <EditableText value={get("footer_phone", "+233 50 123 4567")} onChange={v => set("footer_phone", v)} className="text-gray-400 text-xs" tag="span" placeholder="Phone" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs shrink-0">Email:</span>
                <EditableText value={get("footer_email", "info@greatmessiahpub.com")} onChange={v => set("footer_email", v)} className="text-gray-400 text-xs" tag="span" placeholder="Email" />
              </div>
            </div>
          </div>
        </div>
        {/* Newsletter */}
        <div className="border-t border-white/10 mt-6 pt-5 text-center">
          <EditableText value={get("footer_newsletter_title", "Stay Updated")} onChange={v => set("footer_newsletter_title", v)} className="text-white text-sm font-semibold" tag="span" placeholder="Newsletter Title" />
          <div className="mt-1">
            <EditableText value={get("footer_newsletter_desc", "Get notified when new mock exams and study materials are released.")} onChange={v => set("footer_newsletter_desc", v)} className="text-gray-400 text-xs" tag="span" placeholder="Newsletter Description" />
          </div>
        </div>
        {/* Bottom */}
        <div className="border-t border-white/10 mt-5 pt-4 text-center">
          <EditableText value={get("footer_copyright", "Great Messiah Publication. All rights reserved.")} onChange={v => set("footer_copyright", v)} className="text-gray-500 text-xs" tag="span" placeholder="Copyright Text" />
          <div className="mt-1">
            <EditableText value={get("footer_syllabus_note", "Aligned with Ghana Education Service (GES) Syllabus 2024 to date")} onChange={v => set("footer_syllabus_note", v)} className="text-gray-600 text-[10px]" tag="span" placeholder="Syllabus Note" />
          </div>
        </div>
      </div>
    </div>
  );
}
